// src/screens/UserHomeScreen.js
import React, { useState, useEffect, useRef } from 'react';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import logo from '../assets/logo.png';
import PerfilUsuario from '../components/user/PerfilUsuario';
import { PLANES_DETALLES } from '../data/planesInfo';
import PlanDetalleCard from '../components/common/PlanDetalleCard';
import FaqUsuario from '../components/user/FaqUsuario';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  increment,
  serverTimestamp
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  FaUser,
  FaClipboardList,
  FaQuestionCircle,
  FaSignOutAlt,
  FaCamera,
  FaDumbbell,
  FaAward,
  FaClock,
  FaLock,
  FaHome
} from 'react-icons/fa';

const APP_PRIMARY = '#b03060';
const MAX_IMAGE_SIZE_MB = 5;

function normalizarTexto(str = '') {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

const obtenerDetallePlan = (planRaw) => {
  if (!planRaw || !PLANES_DETALLES) return null;
  if (PLANES_DETALLES[planRaw]) return PLANES_DETALLES[planRaw];

  const p = normalizarTexto(String(planRaw));
  const coincidencias = Object.entries(PLANES_DETALLES);

  for (const [clave, detalle] of coincidencias) {
    const claveNorm = normalizarTexto(clave);
    const tituloNorm = normalizarTexto(detalle?.titulo || '');
    if (claveNorm === p || tituloNorm === p) return detalle;
  }

  const subMatch = coincidencias.find(([clave, detalle]) => {
    const claveNorm = normalizarTexto(clave);
    const tituloNorm = normalizarTexto(detalle?.titulo || '');
    return p.includes(claveNorm) || p.includes(tituloNorm) || tituloNorm.includes(p);
  });

  if (subMatch) return subMatch[1];

  if (p.includes('trial') || p.includes('7') || p.includes('seman')) {
    return PLANES_DETALLES['trial'] || PLANES_DETALLES['semanal'] || PLANES_DETALLES['Plan Semanal (7 días)'] || null;
  }
  if (p.includes('mensual') || p.includes('monthly') || p === 'mes') {
    return PLANES_DETALLES['mensual'] || PLANES_DETALLES['Plan Mensual'] || null;
  }
  if (p.includes('semestr')) {
    return PLANES_DETALLES['semestral'] || PLANES_DETALLES['Plan Semestral'] || null;
  }
  if (p.includes('anual') || p.includes('ano') || p.includes('year')) {
    return PLANES_DETALLES['anual'] || PLANES_DETALLES['Plan Anual'] || null;
  }

  return PLANES_DETALLES['mensual'] || Object.values(PLANES_DETALLES)[0] || null;
};

const formatearNombrePlan = (planRaw) => {
  if (!planRaw) return 'Plan Activo';
  const p = String(planRaw).trim();
  const pLower = normalizarTexto(p);

  if (pLower === 'trial' || pLower === 'semanal' || (pLower.includes('7') && pLower.includes('dias'))) {
    return 'Plan Semanal (7 días)';
  }
  if (pLower === 'mensual' || pLower === 'monthly' || pLower === 'mes') {
    return 'Plan Mensual';
  }
  if (pLower === 'semestral' || pLower === 'semestre') {
    return 'Plan Semestral';
  }
  if (pLower === 'anual' || pLower === 'ano') {
    return 'Plan Anual';
  }

  return p;
};

function VideoPlayerSeguro({ video, registrarVista }) {
  const [urlSegura, setUrlSegura] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [errorCarga, setErrorCarga] = useState(null);

  useEffect(() => {
    let activo = true;

    const obtenerUrl = async () => {
      if (!video.storagePath) {
        setUrlSegura(video.url);
        return;
      }

      setCargando(true);
      setErrorCarga(null);

      try {
        const functions = getFunctions();
        const obtenerVideoSeguro = httpsCallable(functions, 'obtenerVideoSeguro');
        const respuesta = await obtenerVideoSeguro({ videoPath: video.storagePath });

        if (activo && respuesta.data?.url) {
          setUrlSegura(respuesta.data.url);
        }
      } catch (err) {
        console.error('Error al generar enlace seguro de reproducción:', err);
        if (activo) {
          setErrorCarga('Acceso no autorizado o sesión expirada.');
          setUrlSegura(video.url || null);
        }
      } finally {
        if (activo) setCargando(false);
      }
    };

    obtenerUrl();

    return () => {
      activo = false;
    };
  }, [video.id, video.storagePath, video.url]);

  if (cargando) {
    return (
      <div style={playerStyles.loadingContainer}>
        <div style={playerStyles.spinner} />
        <span style={playerStyles.loadingText}>Cargando video protegido...</span>
      </div>
    );
  }

  if (errorCarga && !urlSegura) {
    return (
      <div style={playerStyles.errorContainer}>
        <FaLock size={20} color="#ef4444" />
        <span style={playerStyles.errorText}>{errorCarga}</span>
      </div>
    );
  }

  return (
    <div style={playerStyles.videoWrapper}>
      <video
        src={urlSegura || video.url}
        controls
        muted
        preload="metadata"
        playsInline
        controlsList="nodownload"
        onContextMenu={(e) => e.preventDefault()}
        onPlay={() => registrarVista(video.id)}
        style={playerStyles.videoElement}
      />
    </div>
  );
}

function AccordionRutinas({ videosAsignados, onToggleRealizado, registrarVista, realizados }) {
  const rutinas = ['Día 1', 'Día 2', 'Día 3', 'Día 4'];
  const [openRutinas, setOpenRutinas] = useState({
    'Día 1': true,
    'Día 2': false,
    'Día 3': false,
    'Día 4': false
  });

  const toggleRutina = (r) => setOpenRutinas((prev) => ({ ...prev, [r]: !prev[r] }));
  const regexTieneNumeroAlInicio = /^\s*\d+\s*[-–.]/;

  if (videosAsignados.length === 0) {
    return (
      <div style={styles.emptyRutinasBox}>
        <div style={styles.emptyIconBox}>
          <FaClock size={28} color={APP_PRIMARY} />
        </div>
        <h3 style={styles.emptyTitle}>Rutina en preparación</h3>
        <p style={styles.emptyDesc}>
          Mariana está configurando tu plan de entrenamiento personalizado. Muy pronto verás aquí tus videos y rutinas explicadas paso a paso.
        </p>
      </div>
    );
  }

  return (
    <div>
      {rutinas.map((rutina) => {
        const videosDelDia = videosAsignados.filter(
          (v) => (v.assignment?.rutina || '') === rutina
        );

        const existenVideosNumerados = videosDelDia.some((v) =>
          regexTieneNumeroAlInicio.test(v.nombreVisible || v.nombreArchivo || '')
        );

        const videosFiltrados = existenVideosNumerados
          ? videosDelDia.filter((v) =>
              regexTieneNumeroAlInicio.test(v.nombreVisible || v.nombreArchivo || '')
            )
          : videosDelDia;

        const mapaUnicos = new Map();
        videosFiltrados.forEach((v) => {
          const claveNombre = (v.nombreVisible || v.nombreArchivo || '').trim().toLowerCase();
          if (!mapaUnicos.has(v.id) && !mapaUnicos.has(claveNombre)) {
            mapaUnicos.set(v.id, v);
          }
        });

        const videosEnRutina = Array.from(mapaUnicos.values()).sort((a, b) => {
          const ordenA = a.assignment?.orden ?? a.assignment?.indiceGlobal ?? null;
          const ordenB = b.assignment?.orden ?? b.assignment?.indiceGlobal ?? null;
          if (ordenA !== null && ordenB !== null) {
            return Number(ordenA) - Number(ordenB);
          }

          const nombreA = a.nombreVisible || a.nombreArchivo || '';
          const nombreB = b.nombreVisible || b.nombreArchivo || '';
          return nombreA.localeCompare(nombreB, 'es', {
            numeric: true,
            sensitivity: 'base'
          });
        });

        const isOpen = openRutinas[rutina];

        return (
          <div key={rutina} style={{ marginBottom: 12 }}>
            <button
              type="button"
              onClick={() => toggleRutina(rutina)}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: isOpen ? APP_PRIMARY : '#fff',
                color: isOpen ? '#fff' : APP_PRIMARY,
                border: `2px solid ${APP_PRIMARY}`,
                borderRadius: 12,
                padding: '16px 20px',
                cursor: 'pointer',
                fontSize: '17px',
                fontWeight: 600,
                transition: 'all 0.3s ease',
                boxShadow: isOpen
                  ? '0 6px 16px rgba(176,48,96,0.25)'
                  : '0 2px 8px rgba(0,0,0,0.05)'
              }}
            >
              <div>
                <strong>{rutina}</strong>
                <span style={{ fontSize: 13, marginLeft: 10, opacity: 0.85 }}>
                  ({videosEnRutina.length} {videosEnRutina.length === 1 ? 'ejercicio' : 'ejercicios'})
                </span>
              </div>
              <span
                style={{
                  fontSize: 22,
                  transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }}
              >
                ›
              </span>
            </button>

            {isOpen && (
              <div style={{ marginTop: 8, padding: '12px 4px' }}>
                {videosEnRutina.length === 0 ? (
                  <div style={{ padding: '16px 12px', color: '#64748b', textAlign: 'center', background: '#f8fafc', borderRadius: 10 }}>
                    No hay ejercicios asignados para este día aún.
                  </div>
                ) : (
                  videosEnRutina.map((video) => {
                    const completado = !!realizados[video.id];

                    return (
                      <div key={video.id} style={{ marginBottom: 26 }}>
                        <div style={styles.videoHeader}>
                          <h3 style={styles.videoTitle}>{video.nombreVisible || '(sin nombre)'}</h3>

                          <button
                            type="button"
                            onClick={() => onToggleRealizado(video.id)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 16px',
                              borderRadius: '50px',
                              border: completado ? '1.5px solid #16a34a' : '1.5px solid #e2e8f0',
                              backgroundColor: completado ? '#f0fdf4' : '#ffffff',
                              color: completado ? '#15803d' : '#64748b',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              letterSpacing: '0.2px',
                              boxShadow: completado
                                ? '0 2px 8px rgba(22, 163, 74, 0.15)'
                                : '0 2px 6px rgba(0, 0, 0, 0.04)',
                              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                              outline: 'none'
                            }}
                          >
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: completado ? '#16a34a' : '#f1f5f9',
                                color: completado ? '#ffffff' : '#94a3b8',
                                fontSize: '11px',
                                fontWeight: '800',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              ✓
                            </span>
                            <span>{completado ? 'Completado' : 'Marcar completado'}</span>
                          </button>
                        </div>

                        <ul style={{ margin: '12px 0 16px 0', paddingLeft: 20 }}>
                          {video.descripcion?.split('•').map(
                            (linea, idx) => linea.trim() && <li key={idx} style={{ marginBottom: 4 }}>{linea.trim()}</li>
                          )}
                        </ul>

                        {video.tipo === 'imagen' ? (
                          <img
                            src={video.url}
                            alt={video.nombreVisible}
                            style={{
                              width: '100%',
                              height: 'auto',
                              maxWidth: '100%',
                              borderRadius: 12,
                              objectFit: 'cover',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                            }}
                          />
                        ) : (
                          <VideoPlayerSeguro
                            video={video}
                            registrarVista={registrarVista}
                          />
                        )}

                        {video.recomendacion && (
                          <div style={styles.recPersonal}>
                            <h4>Recomendación personalizada del entrenador:</h4>
                            <p style={{ whiteSpace: 'pre-line' }}>{video.recomendacion}</p>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function UserHomeScreen() {
  const navigate = useNavigate();
  const montadoRef = useRef(true);

  const [section, setSection] = useState('inicio');
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [cargandoNombre, setCargandoNombre] = useState(true);
  const [videosAsignados, setVideosAsignados] = useState([]);
  const [realizados, setRealizados] = useState({});
  const [fotoPreview, setFotoPreview] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [planDetalleInfo, setPlanDetalleInfo] = useState(null);

  const fileInputRef = useRef();
  const reproducidosRef = useRef(new Set());

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (usuario) => {
      if (!usuario) {
        navigate('/login', { replace: true });
        return;
      }

      const uid = usuario.uid;

      try {
        const docRefUsuario = doc(db, 'usuarios', uid);
        const docSnap = await getDoc(docRefUsuario);

        if (!montadoRef.current) return;

        if (!docSnap.exists()) {
          navigate('/plans', { replace: true });
          return;
        }

        const userDocData = docSnap.data();
        const esAdmin = userDocData.isAdmin === true || userDocData.rol === 'admin';
        const estado = (userDocData.subscriptionStatus || userDocData.estado || '').toLowerCase();

        const estaEliminadoOInactivo =
          userDocData.eliminado === true ||
          userDocData.isDeleted === true ||
          userDocData.status === 'deleted' ||
          estado === 'inactivo' ||
          estado === 'eliminado';

        if (!esAdmin && estaEliminadoOInactivo) {
          navigate('/plans', { replace: true });
          return;
        }

        if (!esAdmin && (estado === 'expired' || estado === 'vencido')) {
          navigate('/plans', { replace: true });
          return;
        }

        if (!esAdmin && (estado === 'pending_payment' || estado === 'pending' || estado === 'payment_sent')) {
          navigate('/pago-pendiente', { replace: true });
          return;
        }

        if (!esAdmin && estado !== 'active' && estado !== 'activo') {
          navigate('/plans', { replace: true });
          return;
        }

        const key = `accesoIncrementado_${uid}`;
        if (!sessionStorage.getItem(key) && !estaEliminadoOInactivo) {
          try {
            await updateDoc(docRefUsuario, { accesos: increment(1), updatedAt: serverTimestamp() });
            sessionStorage.setItem(key, 'true');
          } catch (err) {
            console.warn('No se pudo registrar incremento de acceso:', err);
          }
        }

        if (!montadoRef.current) return;

        setNombre(userDocData.nombre || '');
        setApellido(userDocData.apellido || '');
        if (userDocData.fotoURL) setFotoPreview(userDocData.fotoURL);

        const rawPlanDuracion =
          userDocData.subscriptionPlan ||
          userDocData.planContratado ||
          userDocData.plan ||
          '';

        const rawPlanEntrenamiento =
          userDocData.planEntrenamiento ||
          userDocData.planSeleccionado ||
          userDocData.planNombreReportado ||
          userDocData.selectedPlan ||
          '';

        const planParaDetalle = rawPlanDuracion || rawPlanEntrenamiento || 'mensual';
        setUserPlan(formatearNombrePlan(rawPlanEntrenamiento || rawPlanDuracion));
        setPlanDetalleInfo(obtenerDetallePlan(planParaDetalle));

        let videosSnap;
        try {
          const qSegura = query(
            collection(db, 'videos'),
            where('assignedToUids', 'array-contains', uid)
          );
          videosSnap = await getDocs(qSegura);
          if (videosSnap.empty) {
            videosSnap = await getDocs(collection(db, 'videos'));
          }
        } catch {
          videosSnap = await getDocs(collection(db, 'videos'));
        }

        if (!montadoRef.current) return;

        const mapaVideosUnicos = new Map();

        videosSnap.forEach((d) => {
          const data = d.data();
          const assigned = data.assignedTo || [];

          if (!Array.isArray(assigned)) return;

          const asignacionValida = assigned.find(
            (a) => (typeof a === 'string' && a === uid) || (typeof a === 'object' && a?.uid === uid)
          );

          if (!asignacionValida) return;

          const rutinaAsignada = (typeof asignacionValida === 'object' && asignacionValida.rutina) || 'Día 1';

          const assignmentObj = {
            uid,
            nombreUsuario: (typeof asignacionValida === 'object' && asignacionValida.nombreUsuario) || '',
            plan: (typeof asignacionValida === 'object' && asignacionValida.plan) || rawPlanEntrenamiento || rawPlanDuracion || '',
            rutina: rutinaAsignada,
            orden: typeof asignacionValida === 'object' ? (asignacionValida.orden ?? null) : null,
            indiceGlobal: typeof asignacionValida === 'object' ? (asignacionValida.indiceGlobal ?? null) : null
          };

          const claveUnica = `${d.id}_${rutinaAsignada}`;

          if (!mapaVideosUnicos.has(claveUnica)) {
            mapaVideosUnicos.set(claveUnica, {
              id: d.id,
              ...data,
              recomendacion: data.recomendaciones?.[uid] || '',
              assignment: assignmentObj,
              asignadoEn: (data.asignadoEn && data.asignadoEn[uid]) || null
            });
          }
        });

        const listaVideosBruta = Array.from(mapaVideosUnicos.values());

        listaVideosBruta.sort((a, b) => {
          const nombreA = a.nombreVisible || a.nombreArchivo || '';
          const nombreB = b.nombreVisible || b.nombreArchivo || '';
          return nombreA.localeCompare(nombreB, 'es', { numeric: true, sensitivity: 'base' });
        });

        if (!montadoRef.current) return;

        setVideosAsignados(listaVideosBruta);

        if (listaVideosBruta.length > 0 && !rawPlanEntrenamiento) {
          const primerPlanAsignado = listaVideosBruta[0]?.assignment?.plan;
          if (primerPlanAsignado) {
            setUserPlan(formatearNombrePlan(primerPlanAsignado));
          }
        }
      } catch (error) {
        console.error('Error al inicializar sesión o rutinas:', error);
      } finally {
        if (montadoRef.current) setCargandoNombre(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const registrarVista = async (videoId) => {
    if (reproducidosRef.current.has(videoId)) return;
    reproducidosRef.current.add(videoId);
    try {
      await updateDoc(doc(db, 'videos', videoId), { vistas: increment(1) });
    } catch (err) {
      console.warn('No se pudo registrar la vista:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/', { replace: true });
    } catch (error) {
      alert('Error al cerrar sesión: ' + error.message);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      alert('Solo se permiten imágenes en formato JPG o PNG.');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      alert(`La imagen no debe superar los ${MAX_IMAGE_SIZE_MB}MB.`);
      return;
    }

    const usuario = auth.currentUser;
    if (!usuario) return;

    const storage = getStorage();
    const path = `mft/fotosPerfil/${usuario.uid}.jpg`;
    const storageReference = storageRef(storage, path);

    try {
      await uploadBytes(storageReference, file);
      const downloadURL = await getDownloadURL(storageReference);
      await updateDoc(doc(db, 'usuarios', usuario.uid), {
        fotoURL: downloadURL,
        updatedAt: serverTimestamp()
      });
      if (montadoRef.current) setFotoPreview(downloadURL);
    } catch (err) {
      console.error('Error subiendo foto de perfil:', err);
      alert('No se pudo actualizar la foto de perfil.');
    }
  };

  const toggleRealizado = (id) =>
    setRealizados((prev) => ({ ...prev, [id]: !prev[id] }));

  const renderSection = () => {
    switch (section) {
      case 'inicio':
        return (
          <div style={homeStyles.wrapper}>
            <div style={homeStyles.brandHeader}>
              <img src={logo} alt="Mariana Fitness Trainer" style={homeStyles.logo} />
            </div>

            <div style={homeStyles.heroCard}>
              <div style={homeStyles.avatarContainer}>
                <div
                  style={homeStyles.avatarWrapper}
                  onClick={() => fileInputRef.current?.click()}
                  title="Haz clic para subir o cambiar tu foto"
                >
                  {fotoPreview ? (
                    <img src={fotoPreview} alt="Foto de perfil" style={homeStyles.avatarImage} />
                  ) : (
                    <FaUser style={homeStyles.avatarFallbackIcon} />
                  )}
                  <div style={homeStyles.cameraBadge}>
                    <FaCamera size={13} color="#ffffff" />
                  </div>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </div>

              {!cargandoNombre && (
                <div style={homeStyles.greetingContainer}>
                  <span style={homeStyles.welcomeBadge}>Panel de Alumno</span>
                  <h1 style={homeStyles.greetingText}>
                    ¡Hola, {nombre ? `${nombre} ${apellido}` : 'Bienvenido/a'}!
                  </h1>
                  {userPlan ? (
                    <div style={homeStyles.planChip}>
                      <FaDumbbell size={13} color={APP_PRIMARY} />
                      <span>{userPlan}</span>
                    </div>
                  ) : (
                    <p style={homeStyles.subText}>Listo para comenzar tu sesión de entrenamiento</p>
                  )}
                </div>
              )}
            </div>

            <div style={homeStyles.quickGrid}>
              <button
                type="button"
                onClick={() => setSection('rutinas')}
                style={homeStyles.quickCard}
              >
                <div style={{ ...homeStyles.quickIconBox, background: '#fdf2f8' }}>
                  <FaClipboardList color={APP_PRIMARY} size={20} />
                </div>
                <div style={homeStyles.quickInfo}>
                  <strong style={homeStyles.quickTitle}>Mis Rutinas</strong>
                  <span style={homeStyles.quickDesc}>Ver ejercicios y videos</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSection('perfil')}
                style={homeStyles.quickCard}
              >
                <div style={{ ...homeStyles.quickIconBox, background: '#f0fdf4' }}>
                  <FaUser color="#16a34a" size={20} />
                </div>
                <div style={homeStyles.quickInfo}>
                  <strong style={homeStyles.quickTitle}>Mi Perfil</strong>
                  <span style={homeStyles.quickDesc}>Datos y ficha técnica</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSection('faq')}
                style={homeStyles.quickCard}
              >
                <div style={{ ...homeStyles.quickIconBox, background: '#eff6ff' }}>
                  <FaQuestionCircle color="#2563eb" size={20} />
                </div>
                <div style={homeStyles.quickInfo}>
                  <strong style={homeStyles.quickTitle}>Preguntas Frecuentes</strong>
                  <span style={homeStyles.quickDesc}>Dudas sobre tu rutina</span>
                </div>
              </button>
            </div>

            <div style={homeStyles.aboutCard}>
              <div style={homeStyles.aboutHeader}>
                <div style={homeStyles.aboutIconBox}>
                  <FaAward color={APP_PRIMARY} size={18} />
                </div>
                <div>
                  <h3 style={homeStyles.aboutTitle}>Mariana Fitness Trainer</h3>
                  <span style={homeStyles.aboutSubtitle}>
                    Entrenamiento personalizado y supervisión técnica
                  </span>
                </div>
              </div>
              <p style={homeStyles.aboutText}>
                Mariana Fitness Trainer es una aplicación diseñada para ofrecer rutinas de ejercicio personalizadas, creadas por <strong>Mariana Retamal</strong>, Licenciada en Educación Física y Personal Trainer con certificaciones en Chile y Alemania. A través de la app, los usuarios reciben planes adaptados a sus objetivos, nivel de condición y disponibilidad horaria, respaldados por explicaciones paso a paso y videos demostrativos.
              </p>
            </div>
          </div>
        );

      case 'perfil':
        return <PerfilUsuario />;

      case 'rutinas': {
        return (
          <div style={styles.card}>
            <h2 style={styles.sectionTitle}>Mis rutinas</h2>

            {planDetalleInfo && (
              <PlanDetalleCard planInfo={planDetalleInfo} />
            )}

            <div style={{ marginTop: 24 }}>
              <AccordionRutinas
                videosAsignados={videosAsignados}
                onToggleRealizado={toggleRealizado}
                registrarVista={registrarVista}
                realizados={realizados}
              />
            </div>
          </div>
        );
      }

      case 'faq':
        return <FaqUsuario />;

      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.mainContent}>{renderSection()}</div>

      <div style={navDockStyles.dockContainer}>
        <div style={navDockStyles.dockPill}>
          <button
            type="button"
            onClick={() => setSection('inicio')}
            style={{
              ...navDockStyles.dockButton,
              backgroundColor: section === 'inicio' ? '#fdf2f8' : 'transparent',
              color: section === 'inicio' ? APP_PRIMARY : '#64748b',
              fontWeight: section === 'inicio' ? '700' : '600'
            }}
          >
            <div
              style={{
                ...navDockStyles.iconWrapper,
                backgroundColor: section === 'inicio' ? APP_PRIMARY : '#f1f5f9',
                color: section === 'inicio' ? '#ffffff' : '#64748b'
              }}
            >
              <FaHome size={15} />
            </div>
            <span>Inicio</span>
          </button>

          <div style={navDockStyles.divider} />

          <button
            type="button"
            onClick={handleLogout}
            style={{
              ...navDockStyles.dockButton,
              color: '#dc2626'
            }}
          >
            <div
              style={{
                ...navDockStyles.iconWrapper,
                backgroundColor: '#fee2e2',
                color: '#dc2626'
              }}
            >
              <FaSignOutAlt size={14} />
            </div>
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const playerStyles = {
  videoWrapper: {
    width: '100%',
    position: 'relative',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#090d16',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)'
  },
  videoElement: {
    width: '100%',
    height: 'auto',
    maxHeight: '480px',
    display: 'block',
    outline: 'none'
  },
  loadingContainer: {
    padding: '36px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    backgroundColor: '#0f172a',
    borderRadius: 12,
    color: '#94a3b8'
  },
  spinner: {
    width: 28,
    height: 28,
    border: '3px solid rgba(255, 255, 255, 0.15)',
    borderTop: `3px solid ${APP_PRIMARY}`,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  },
  loadingText: {
    fontSize: '13px',
    fontWeight: '500'
  },
  errorContainer: {
    padding: '24px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    backgroundColor: '#fef2f2',
    border: '1px solid #fee2e2',
    borderRadius: 12
  },
  errorText: {
    color: '#991b1b',
    fontSize: '13px',
    fontWeight: '600'
  }
};

const homeStyles = {
  wrapper: {
    maxWidth: 780,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  },
  brandHeader: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 0 2px 0'
  },
  logo: {
    height: 72,
    width: 'auto',
    objectFit: 'contain'
  },
  heroCard: {
    background: '#ffffff',
    borderRadius: 22,
    padding: '36px 24px 28px 24px',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    border: '1px solid #f1f5f9'
  },
  avatarContainer: {
    position: 'relative',
    marginBottom: 16
  },
  avatarWrapper: {
    width: 130,
    height: 130,
    borderRadius: '50%',
    padding: 4,
    background: `linear-gradient(135deg, ${APP_PRIMARY}, #f472b6)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
    boxShadow: '0 8px 20px rgba(176, 48, 96, 0.22)',
    transition: 'transform 0.25s ease'
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid #ffffff'
  },
  avatarFallbackIcon: {
    fontSize: 54,
    color: '#ffffff'
  },
  cameraBadge: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: APP_PRIMARY,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2.5px solid #ffffff',
    boxShadow: '0 2px 6px rgba(0,0,0,0.18)'
  },
  greetingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8
  },
  welcomeBadge: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: APP_PRIMARY,
    background: '#fdf2f8',
    padding: '4px 12px',
    borderRadius: 20
  },
  greetingText: {
    fontSize: 26,
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
    letterSpacing: '-0.3px',
    fontFamily: "'Poppins', sans-serif"
  },
  subText: {
    fontSize: 14,
    color: '#64748b',
    margin: 0
  },
  planChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: '#fdf2f8',
    color: APP_PRIMARY,
    border: '1px solid #fbcfe8',
    padding: '6px 14px',
    borderRadius: 50,
    fontSize: 13,
    fontWeight: '600',
    marginTop: 4
  },
  quickGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 14
  },
  quickCard: {
    background: '#ffffff',
    border: '1px solid #f1f5f9',
    borderRadius: 16,
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    textAlign: 'left'
  },
  quickIconBox: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  quickInfo: {
    display: 'flex',
    flexDirection: 'column'
  },
  quickTitle: {
    fontSize: 14,
    color: '#1e293b',
    fontWeight: '600'
  },
  quickDesc: {
    fontSize: 12,
    color: '#94a3b8'
  },
  aboutCard: {
    background: '#ffffff',
    borderRadius: 18,
    padding: '24px',
    border: '1px solid #f1f5f9',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.03)'
  },
  aboutHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12
  },
  aboutIconBox: {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: '#fdf2f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  aboutTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: '700',
    color: '#1e293b'
  },
  aboutSubtitle: {
    fontSize: 12,
    color: '#64748b'
  },
  aboutText: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.7,
    color: '#475569'
  }
};

const navDockStyles = {
  dockContainer: {
    position: 'fixed',
    bottom: 20,
    left: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    pointerEvents: 'none',
    padding: '0 16px',
    boxSizing: 'border-box'
  },
  dockPill: {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.94)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1.5px solid rgba(241, 245, 249, 0.95)',
    borderRadius: 50,
    padding: '6px 12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
  },
  dockButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    borderRadius: 50,
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: "'Poppins', sans-serif",
    transition: 'all 0.2s ease',
    outline: 'none'
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease'
  },
  divider: {
    width: 1,
    height: 22,
    backgroundColor: '#e2e8f0',
    margin: '0 2px'
  }
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5'
  },
  mainContent: { flex: 1, padding: 20, paddingBottom: 100 },
  card: {
    maxWidth: 900,
    margin: '0 auto',
    backgroundColor: '#fff',
    borderRadius: 20,
    boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
    padding: 32
  },
  sectionTitle: { textAlign: 'center', color: APP_PRIMARY, fontSize: 28, marginBottom: 20 },
  recPersonal: {
    marginTop: 12,
    backgroundColor: '#fff6f9',
    padding: '12px 16px',
    borderLeft: `4px solid ${APP_PRIMARY}`,
    borderRadius: 8,
    color: '#333'
  },
  videoHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 },
  videoTitle: { fontSize: 20, color: '#333', margin: 0 },
  emptyRutinasBox: {
    textAlign: 'center',
    padding: '36px 20px',
    backgroundColor: '#fdf2f8',
    borderRadius: 16,
    border: '1.5px dashed #fbcfe8',
    margin: '12px 0'
  },
  emptyIconBox: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 14px',
    boxShadow: '0 4px 12px rgba(176, 48, 96, 0.12)'
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 8px 0'
  },
  emptyDesc: {
    fontSize: 14,
    color: '#64748b',
    maxWidth: 520,
    margin: '0 auto',
    lineHeight: 1.6
  }
};